import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Clock, 
  Home, 
  ArrowLeft,
  Loader2,
  AlertTriangle,
  Euro,
  TrendingUp,
  TrendingDown,
  Scale,
  User,
  Users
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Page de statistiques de rentabilité des logements et FDM
 * 3 catégories claires : SUR-PAYÉES (rouge) / ÉQUILIBRÉ (jaune) / RENTABLES (vert)
 */
const CleaningTimeStats = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('properties'); // 'properties' ou 'housekeepers'

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_URL}/api/stats/cleaning-time`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!response.ok) {
          throw new Error('Erreur lors du chargement des statistiques');
        }
        
        const data = await response.json();
        setStats(data);
      } catch (err) {
        console.error('Error fetching stats:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [token]);

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <main className="flex-1 p-6 overflow-auto flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-slate-600">Chargement des statistiques...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <main className="flex-1 p-6 overflow-auto flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="p-8 text-center">
              <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Erreur</h3>
              <p className="text-slate-600 mb-4">{error}</p>
              <Button onClick={() => window.location.reload()}>Réessayer</Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Catégoriser les logements - Seuil de tolérance : ±5€
  const TOLERANCE_EUROS = 5;
  
  const allProperties = [
    ...(stats?.top_longest || []),
    ...(stats?.top_fastest || [])
  ];
  
  // Dédupliquer par property_id
  const uniqueProperties = [];
  const seen = new Set();
  for (const prop of allProperties) {
    if (!seen.has(prop.property_id)) {
      seen.add(prop.property_id);
      uniqueProperties.push(prop);
    }
  }

  // PERTE : Forfait > Coût réel (différence positive = vous payez trop la FDM)
  const lossProperties = uniqueProperties.filter(p => 
    p.avg_difference > TOLERANCE_EUROS
  ).sort((a, b) => b.avg_difference - a.avg_difference); // Plus gros surpaiement en premier

  // RENTABLE : Forfait < Coût réel (différence négative = vous payez moins que la valeur)
  const profitProperties = uniqueProperties.filter(p => 
    p.avg_difference < -TOLERANCE_EUROS
  ).sort((a, b) => a.avg_difference - b.avg_difference); // Meilleure affaire en premier

  // ÉQUILIBRÉ : Dans la tolérance
  const balancedProperties = uniqueProperties.filter(p => 
    Math.abs(p.avg_difference) <= TOLERANCE_EUROS
  ).sort((a, b) => a.name.localeCompare(b.name));

  // Calcul des totaux par catégorie
  const totalLoss = lossProperties.reduce((sum, p) => sum + (p.avg_difference * p.count), 0);
  const totalProfit = profitProperties.reduce((sum, p) => sum + (p.avg_difference * p.count), 0);

  // ========== STATS PAR FDM ==========
  const housekeepers = stats?.by_housekeeper || [];
  
  // FDM sur-payées (vous payez trop)
  const overpaidHousekeepers = housekeepers.filter(h => h.avg_difference > TOLERANCE_EUROS)
    .sort((a, b) => b.avg_difference - a.avg_difference);
  
  // FDM équilibrées
  const balancedHousekeepers = housekeepers.filter(h => Math.abs(h.avg_difference) <= TOLERANCE_EUROS)
    .sort((a, b) => a.name.localeCompare(b.name));
  
  // FDM rentables (vous économisez)
  const profitableHousekeepers = housekeepers.filter(h => h.avg_difference < -TOLERANCE_EUROS)
    .sort((a, b) => a.avg_difference - b.avg_difference);

  const totalLossHK = overpaidHousekeepers.reduce((sum, h) => sum + h.total_difference, 0);
  const totalProfitHK = profitableHousekeepers.reduce((sum, h) => sum + Math.abs(h.total_difference), 0);

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 p-4 md:p-6 overflow-auto">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/admin')}
              className="rounded-full"
              data-testid="back-button"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl md:text-2xl font-bold text-slate-800">
                Analyse de Rentabilité
              </h1>
              <p className="text-sm text-slate-500">
                Base : 32€/heure • {stats?.total_tasks_analyzed || 0} prestations analysées
              </p>
            </div>
            {/* Toggle Logements / FDM */}
            <div className="flex bg-slate-100 rounded-lg p-1">
              <Button
                variant={viewMode === 'properties' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('properties')}
                className={`gap-2 ${viewMode === 'properties' ? '' : 'text-slate-600'}`}
                data-testid="view-properties-btn"
              >
                <Home className="h-4 w-4" />
                Logements
              </Button>
              <Button
                variant={viewMode === 'housekeepers' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('housekeepers')}
                className={`gap-2 ${viewMode === 'housekeepers' ? '' : 'text-slate-600'}`}
                data-testid="view-housekeepers-btn"
              >
                <Users className="h-4 w-4" />
                FDM
              </Button>
            </div>
          </div>

          {/* Résumé global */}
          <Card className="mb-6 border-0 shadow-lg bg-gradient-to-r from-slate-800 to-slate-900 text-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-300 text-sm mb-1">Bilan : Forfaits payés vs Valeur réelle</p>
                  <p className={`text-3xl font-bold ${stats?.total_difference <= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {stats?.total_difference > 0 ? '+' : ''}{stats?.total_difference?.toFixed(0)}€
                  </p>
                  <div className="flex gap-4 mt-2 text-sm">
                    <span className="text-slate-400">Forfaits payés: <strong className="text-white">{stats?.total_forfait?.toFixed(0)}€</strong></span>
                    <span className="text-slate-400">Valeur travail: <strong className="text-white">{stats?.total_real_cost?.toFixed(0)}€</strong></span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    {stats?.total_difference > 0 
                      ? "⚠️ Vous payez plus que la valeur du travail" 
                      : stats?.total_difference < 0 
                        ? "✅ Vous payez moins que la valeur du travail"
                        : "Équilibré"}
                  </p>
                </div>
                <div className="text-right">
                  <Euro className="h-12 w-12 text-slate-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 3 Sections en grille - LOGEMENTS */}
          {viewMode === 'properties' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            
            {/* SECTION ROUGE - PERTES (vous payez trop) */}
            <Card className="border-2 border-red-200 bg-red-50/50 shadow-md" data-testid="loss-section">
              <CardHeader className="pb-3 bg-red-100/50">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                      <TrendingDown className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <span className="text-red-700 font-bold">SUR-PAYÉS</span>
                      <p className="text-xs text-red-600 font-normal">Forfait trop élevé</p>
                    </div>
                  </div>
                  <Badge className="bg-red-500 text-white text-lg px-3">{lossProperties.length}</Badge>
                </CardTitle>
                {lossProperties.length > 0 && (
                  <p className="text-red-700 text-sm font-semibold mt-2">
                    Vous payez {totalLoss.toFixed(0)}€ de trop
                  </p>
                )}
              </CardHeader>
              <CardContent className="pt-4 max-h-[400px] overflow-y-auto">
                {lossProperties.length > 0 ? (
                  <div className="space-y-3">
                    {lossProperties.map(prop => (
                      <div key={prop.property_id} className="bg-white rounded-lg p-3 border border-red-200 shadow-sm">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-slate-800 truncate text-sm">{prop.name}</h4>
                            <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                              <Clock className="h-3 w-3" />
                              {prop.avg_formatted} • {prop.count} ménages
                            </p>
                          </div>
                          <Badge className="bg-red-100 text-red-700 font-bold">
                            +{prop.avg_difference?.toFixed(0)}€
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-center mt-2">
                          <div className="bg-red-50 rounded p-1.5">
                            <p className="text-[10px] text-slate-500 uppercase">Forfait payé</p>
                            <p className="font-bold text-red-600 text-sm">{prop.avg_forfait?.toFixed(0)}€</p>
                          </div>
                          <div className="bg-red-50 rounded p-1.5">
                            <p className="text-[10px] text-slate-500 uppercase">Valeur réelle</p>
                            <p className="font-bold text-slate-800 text-sm">{prop.avg_real_cost?.toFixed(0)}€</p>
                          </div>
                        </div>
                        <div className="mt-2 text-center bg-red-100 rounded p-1.5">
                          <p className="text-[10px] text-red-600">Forfait suggéré</p>
                          <p className="font-bold text-red-700">{Math.ceil(prop.avg_real_cost * 1.05)}€</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <TrendingUp className="h-10 w-10 mx-auto mb-2 text-green-400" />
                    <p className="text-sm">Aucune FDM sur-payée !</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* SECTION JAUNE - ÉQUILIBRÉ */}
            <Card className="border-2 border-amber-200 bg-amber-50/50 shadow-md" data-testid="balanced-section">
              <CardHeader className="pb-3 bg-amber-100/50">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
                      <Scale className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <span className="text-amber-700 font-bold">ÉQUILIBRÉ</span>
                      <p className="text-xs text-amber-600 font-normal">Tarif correct (±5€)</p>
                    </div>
                  </div>
                  <Badge className="bg-amber-500 text-white text-lg px-3">{balancedProperties.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 max-h-[400px] overflow-y-auto">
                {balancedProperties.length > 0 ? (
                  <div className="space-y-3">
                    {balancedProperties.map(prop => (
                      <div key={prop.property_id} className="bg-white rounded-lg p-3 border border-amber-200 shadow-sm">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-slate-800 truncate text-sm">{prop.name}</h4>
                            <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                              <Clock className="h-3 w-3" />
                              {prop.avg_formatted} • {prop.count} ménages
                            </p>
                          </div>
                          <Badge className="bg-amber-100 text-amber-700 font-bold">
                            {prop.avg_difference > 0 ? '+' : ''}{prop.avg_difference?.toFixed(0)}€
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-center mt-2">
                          <div className="bg-amber-50 rounded p-1.5">
                            <p className="text-[10px] text-slate-500 uppercase">Forfait</p>
                            <p className="font-bold text-slate-800 text-sm">{prop.avg_forfait?.toFixed(0)}€</p>
                          </div>
                          <div className="bg-amber-50 rounded p-1.5">
                            <p className="text-[10px] text-slate-500 uppercase">Coût réel</p>
                            <p className="font-bold text-slate-800 text-sm">{prop.avg_real_cost?.toFixed(0)}€</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Home className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">Aucun logement équilibré</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* SECTION VERTE - RENTABLE (vous payez moins que la valeur) */}
            <Card className="border-2 border-emerald-200 bg-emerald-50/50 shadow-md" data-testid="profit-section">
              <CardHeader className="pb-3 bg-emerald-100/50">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <span className="text-emerald-700 font-bold">RENTABLES</span>
                      <p className="text-xs text-emerald-600 font-normal">Bon rapport qualité/prix</p>
                    </div>
                  </div>
                  <Badge className="bg-emerald-500 text-white text-lg px-3">{profitProperties.length}</Badge>
                </CardTitle>
                {profitProperties.length > 0 && (
                  <p className="text-emerald-700 text-sm font-semibold mt-2">
                    Vous économisez {Math.abs(totalProfit).toFixed(0)}€
                  </p>
                )}
              </CardHeader>
              <CardContent className="pt-4 max-h-[400px] overflow-y-auto">
                {profitProperties.length > 0 ? (
                  <div className="space-y-3">
                    {profitProperties.map(prop => (
                      <div key={prop.property_id} className="bg-white rounded-lg p-3 border border-emerald-200 shadow-sm">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-slate-800 truncate text-sm">{prop.name}</h4>
                            <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                              <Clock className="h-3 w-3" />
                              {prop.avg_formatted} • {prop.count} ménages
                            </p>
                          </div>
                          <Badge className="bg-emerald-100 text-emerald-700 font-bold">
                            {prop.avg_difference?.toFixed(0)}€
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-center mt-2">
                          <div className="bg-emerald-50 rounded p-1.5">
                            <p className="text-[10px] text-slate-500 uppercase">Forfait payé</p>
                            <p className="font-bold text-emerald-600 text-sm">{prop.avg_forfait?.toFixed(0)}€</p>
                          </div>
                          <div className="bg-emerald-50 rounded p-1.5">
                            <p className="text-[10px] text-slate-500 uppercase">Valeur réelle</p>
                            <p className="font-bold text-slate-800 text-sm">{prop.avg_real_cost?.toFixed(0)}€</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <TrendingDown className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">Aucun logement très rentable</p>
                  </div>
                )}
              </CardContent>
            </Card>

          </div>
          )}

          {/* 3 Sections en grille - FDM */}
          {viewMode === 'housekeepers' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            
            {/* SECTION ROUGE - FDM SUR-PAYÉES */}
            <Card className="border-2 border-red-200 bg-red-50/50 shadow-md" data-testid="loss-hk-section">
              <CardHeader className="pb-3 bg-red-100/50">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                      <User className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <span className="text-red-700 font-bold">SUR-PAYÉES</span>
                      <p className="text-xs text-red-600 font-normal">Trop payées vs temps</p>
                    </div>
                  </div>
                  <Badge className="bg-red-500 text-white text-lg px-3">{overpaidHousekeepers.length}</Badge>
                </CardTitle>
                {overpaidHousekeepers.length > 0 && (
                  <p className="text-red-700 text-sm font-semibold mt-2">
                    Surcoût total : +{totalLossHK.toFixed(0)}€
                  </p>
                )}
              </CardHeader>
              <CardContent className="pt-4 max-h-[400px] overflow-y-auto">
                {overpaidHousekeepers.length > 0 ? (
                  <div className="space-y-3">
                    {overpaidHousekeepers.map(hk => (
                      <div key={hk.housekeeper_id} className="bg-white rounded-lg p-3 border border-red-200 shadow-sm">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-slate-800 truncate text-sm">{hk.name}</h4>
                            <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                              <Clock className="h-3 w-3" />
                              {hk.avg_formatted} moy • {hk.count} ménages
                            </p>
                          </div>
                          <Badge className="bg-red-100 text-red-700 font-bold">
                            +{hk.avg_difference?.toFixed(0)}€/ménage
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-center mt-2">
                          <div className="bg-red-50 rounded p-1.5">
                            <p className="text-[10px] text-slate-500 uppercase">Forfait moy.</p>
                            <p className="font-bold text-red-600 text-sm">{hk.avg_forfait?.toFixed(0)}€</p>
                          </div>
                          <div className="bg-red-50 rounded p-1.5">
                            <p className="text-[10px] text-slate-500 uppercase">Valeur réelle</p>
                            <p className="font-bold text-slate-800 text-sm">{hk.avg_real_cost?.toFixed(0)}€</p>
                          </div>
                        </div>
                        <div className="mt-2 text-center bg-red-100 rounded p-1.5">
                          <p className="text-[10px] text-red-600">Surcoût total</p>
                          <p className="font-bold text-red-700">+{hk.total_difference?.toFixed(0)}€</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <TrendingUp className="h-10 w-10 mx-auto mb-2 text-green-400" />
                    <p className="text-sm">Aucune FDM sur-payée !</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* SECTION JAUNE - FDM ÉQUILIBRÉES */}
            <Card className="border-2 border-amber-200 bg-amber-50/50 shadow-md" data-testid="balanced-hk-section">
              <CardHeader className="pb-3 bg-amber-100/50">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
                      <Scale className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <span className="text-amber-700 font-bold">ÉQUILIBRÉES</span>
                      <p className="text-xs text-amber-600 font-normal">Tarif juste (±5€)</p>
                    </div>
                  </div>
                  <Badge className="bg-amber-500 text-white text-lg px-3">{balancedHousekeepers.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 max-h-[400px] overflow-y-auto">
                {balancedHousekeepers.length > 0 ? (
                  <div className="space-y-3">
                    {balancedHousekeepers.map(hk => (
                      <div key={hk.housekeeper_id} className="bg-white rounded-lg p-3 border border-amber-200 shadow-sm">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-slate-800 truncate text-sm">{hk.name}</h4>
                            <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                              <Clock className="h-3 w-3" />
                              {hk.avg_formatted} moy • {hk.count} ménages
                            </p>
                          </div>
                          <Badge className="bg-amber-100 text-amber-700 font-bold">
                            {hk.avg_difference > 0 ? '+' : ''}{hk.avg_difference?.toFixed(0)}€
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-center mt-2">
                          <div className="bg-amber-50 rounded p-1.5">
                            <p className="text-[10px] text-slate-500 uppercase">Forfait moy.</p>
                            <p className="font-bold text-slate-800 text-sm">{hk.avg_forfait?.toFixed(0)}€</p>
                          </div>
                          <div className="bg-amber-50 rounded p-1.5">
                            <p className="text-[10px] text-slate-500 uppercase">Valeur réelle</p>
                            <p className="font-bold text-slate-800 text-sm">{hk.avg_real_cost?.toFixed(0)}€</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <User className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">Aucune FDM équilibrée</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* SECTION VERTE - FDM RENTABLES */}
            <Card className="border-2 border-emerald-200 bg-emerald-50/50 shadow-md" data-testid="profit-hk-section">
              <CardHeader className="pb-3 bg-emerald-100/50">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <span className="text-emerald-700 font-bold">RENTABLES</span>
                      <p className="text-xs text-emerald-600 font-normal">Bon rapport temps/prix</p>
                    </div>
                  </div>
                  <Badge className="bg-emerald-500 text-white text-lg px-3">{profitableHousekeepers.length}</Badge>
                </CardTitle>
                {profitableHousekeepers.length > 0 && (
                  <p className="text-emerald-700 text-sm font-semibold mt-2">
                    Économie totale : {totalProfitHK.toFixed(0)}€
                  </p>
                )}
              </CardHeader>
              <CardContent className="pt-4 max-h-[400px] overflow-y-auto">
                {profitableHousekeepers.length > 0 ? (
                  <div className="space-y-3">
                    {profitableHousekeepers.map(hk => (
                      <div key={hk.housekeeper_id} className="bg-white rounded-lg p-3 border border-emerald-200 shadow-sm">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-slate-800 truncate text-sm">{hk.name}</h4>
                            <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                              <Clock className="h-3 w-3" />
                              {hk.avg_formatted} moy • {hk.count} ménages
                            </p>
                          </div>
                          <Badge className="bg-emerald-100 text-emerald-700 font-bold">
                            {hk.avg_difference?.toFixed(0)}€/ménage
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-center mt-2">
                          <div className="bg-emerald-50 rounded p-1.5">
                            <p className="text-[10px] text-slate-500 uppercase">Forfait moy.</p>
                            <p className="font-bold text-emerald-600 text-sm">{hk.avg_forfait?.toFixed(0)}€</p>
                          </div>
                          <div className="bg-emerald-50 rounded p-1.5">
                            <p className="text-[10px] text-slate-500 uppercase">Valeur réelle</p>
                            <p className="font-bold text-slate-800 text-sm">{hk.avg_real_cost?.toFixed(0)}€</p>
                          </div>
                        </div>
                        <div className="mt-2 text-center bg-emerald-100 rounded p-1.5">
                          <p className="text-[10px] text-emerald-600">Économie totale</p>
                          <p className="font-bold text-emerald-700">{Math.abs(hk.total_difference)?.toFixed(0)}€</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <TrendingDown className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">Aucune FDM très rentable</p>
                  </div>
                )}
              </CardContent>
            </Card>

          </div>
          )}

          {/* Légende */}
          <p className="text-xs text-center text-slate-400 mt-6">
            Calcul : Forfait payé - (Temps passé × 32€/h) • Si positif = sur-payé • Tolérance : ±5€
          </p>
        </div>
      </main>
    </div>
  );
};

export default CleaningTimeStats;
